import { FormValues } from 'src/features/createDeviceConfiguration/type/FormValues';
import { ENABLED_SENSOR_TYPES } from 'src/features/createDeviceConfiguration/data/initialData';

/**
 * `newEntityValues` guarda el row crudo de Kysely (getRecordById), NO el shape
 * mapeado que devuelve la query GQL de carga inicial: los campos de opción llegan
 * como pares planos `<campo>OptionId`/`<campo>OptionName` (a veces con un nombre de
 * columna distinto al del campo GQL, ver mapper.ts backend) en vez de `<campo>Option: {id, name}`.
 * Replica `mapOption()` de `backend/src/services/deviceProfile/mapper.ts`.
 */
const mapOption = (id: number | null | undefined, name: string | null | undefined) =>
  id == null ? null : { id, name: name ?? '' };

/**
 * Mapea el shape del DB (newEntityValues de auditLogDetails) al shape del form (FormValues).
 * El snapshot guarda el mismo shape que devuelve GET_UPDATE_HARDWARE_CONFIGURATION_DATA,
 * por lo que el mapper es casi identity para los campos de hardwareConfiguration.
 */
export function mapDbConfigToFormValues(dbConfig: any): Partial<FormValues> {
  if (!dbConfig) return {};

  // El snapshot puede venir como el objeto hardwareConfiguration directamente,
  // o anidado dentro de { hardwareConfiguration: {...}, sensors: [...] }
  const hc = dbConfig.hardwareConfiguration ?? dbConfig;
  const sensors: any[] = dbConfig.sensors ?? [];

  return {
    images: hc.images ? { url: hc.images } : null,
    nameProject: hc.nameProject ?? null,
    googleDriveUrl: hc.googleDriveUrl ?? null,
    electricalPlanUrl: hc.electricalPlanUrl ?? null,
    diagramUrl: hc.diagramUrl ?? null,
    electricalPlanFileKey: null,
    diagramFileKey: null,
    dateAction: hc.dateAction ? new Date(hc.dateAction) : new Date(),
    observation: hc.observation ?? null,
    lockNumber: hc.lockNumber ?? null,

    // Energization
    energizationOption: mapOption(hc.energizationOptionId, hc.energizationOptionName),
    powerSupplyVoltageOption: mapOption(hc.powerSupplyOptionId, hc.powerSupplyOptionName),
    powerSupplyModel: hc.powerSupplyModel ?? null,
    powerSupplySerialNumber: hc.powerSupplySerialNumber ?? null,
    powerSupplyBrand: hc.powerSupplyBrand ?? null,
    solarControllerModelOption: mapOption(hc.solarControllerModelOptionId, hc.solarControllerModelOptionName),
    numberSolarPanelsControllers: hc.numberSolarPanelsControllers ?? null,
    batchNumberSolarController: hc.batchNumberSolarController ?? null,
    quantityBattery: hc.quantityBattery ?? null,
    modelBattery: hc.modelBattery ?? null,
    technologyOption: mapOption(hc.technologyOptionId, hc.technologyOptionName),
    batteryCapacityOption: mapOption(hc.batteryOptionId, hc.batteryOptionName),
    batteryVoltageOption: mapOption(hc.voltageOptionId, hc.voltageOptionName),
    quantitySun: hc.quantitySun ?? null,
    modelsSun: hc.modelsSun ?? null,
    watts: hc.watts ?? null,
    numberSun: hc.numberSun ?? null,

    // Data logger
    cpuOption: mapOption(hc.cpuOptionId, hc.cpuOptionName),
    batchNumberCpu: hc.batchNumberCpu ?? null,
    auxiliaryOption: mapOption(hc.auxiliaryOptionId, hc.auxiliaryOptionName),
    numberAuxiliary: hc.numberAuxiliary ?? null,
    batchNumberAuxiliary: hc.batchNumberAuxiliary ?? null,
    codeNumberWidgetlord: hc.codeNumberWidgetlord ?? null,
    serialNumberWidgetlord: hc.serialNumberWidgetlord ?? null,
    batchNumberWidgetlord: hc.batchNumberWidgetlord ?? null,
    imageVersion: hc.imageVersion ?? null,
    usbExtraction: !!hc.usbExtraction,
    tcpExtraction: !!hc.tcpExtraction,
    tcpExtractionIp: hc.tcpExtractionIp ?? null,
    ethernetMaintenance: hc.ethernetMaintenance ?? null,
    ethernetMaintenanceEnabled: !!hc.ethernetMaintenance,
    signalReaderOption: mapOption(hc.signalReaderOptionId, hc.signalReaderOptionName),
    signalReaderChannel: hc.signalReaderChannel ?? null,
    flowPulse: !!hc.flowPulse,
    flowPulseTdiOption: mapOption(hc.flowPulseTdiOptionId, hc.flowPulseTdiOptionName),
    flowPulseVoltage: hc.flowPulseVoltage ?? null,
    flowPulseTdiChannel: hc.flowPulseTdiChannel ?? null,

    // Connectivity
    dataConnectionOption: mapOption(hc.dataConnectionTypeOptionId, hc.dataConnectionTypeOptionName),
    internetConnectionOption: mapOption(hc.internetConnectionOptionId, hc.internetConnectionOptionName),
    modelsConnectionOption: mapOption(hc.modelsConnectionOptionId, hc.modelsConnectionOptionName),
    batchNumberSimCard: hc.batchNumberSimCard ?? null,
    approved: !!hc.approved,
    mobileOperatorOption: mapOption(hc.mobileOperatorOptionId, hc.mobileOperatorOptionName),
    idEmnify: hc.idEmnify ?? null,
    dataPlanMobileOption: mapOption(hc.dataPlanOptionId, hc.dataPlanOptionName),
    antennaOption: mapOption(hc.antennaOptionId, hc.antennaOptionName),
    ip: hc.ip ?? null,
    mac: hc.mac ?? null,
    configurationIp: hc.configurationIp ?? null,
    administrator: hc.administrator ?? null,
    networkName: hc.networkName ?? null,
    iccid: hc.iccid ?? null,
    imei: hc.imei ?? null,
    satellitalTypeOption: mapOption(hc.satellitalTypeOptionId, hc.satellitalTypeOptionName),
    phoneNumber: hc.phoneNumber ?? null,
    satellitalSv: hc.satellitalSv ?? null,
    satellitalHv: hc.satellitalHv ?? null,
    satellitalHardwareName: hc.satellitalHardwareName ?? null,
    satellitalSerialName: hc.satellitalSerialName ?? null,
    cableNetworkType: hc.cableNetworkType ?? null,

    // Sensors: mapear los sensors del snapshot filtrando por ENABLED_SENSOR_TYPES
    sensors: sensors
      .filter(s => ENABLED_SENSOR_TYPES.includes(s?.type?.label ?? s?.sensorType ?? ''))
      .map(s => {
        const cfg = s.configuration ?? s;
        return {
          id: s.id ?? 0,
          sensorModelOption: cfg.sensorModelOption ?? null,
          serialNumber: cfg.serialNumber ?? null,
          modbusId: cfg.modbusId ?? null,
          batchNumber: cfg.batchNumber ?? null,
          integrationDetail: cfg.integrationDetail ?? null,
          factorM: cfg.factorM ?? null,
          offsetN: cfg.offsetN ?? null,
          attributes: cfg.attributes
            ? typeof cfg.attributes === 'string'
              ? JSON.parse(cfg.attributes)
              : cfg.attributes
            : null,
        };
      }),
  };
}
